import { ArrowDownIcon, ArrowRightIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'

import person from '../../../../../assets/images/illustrations/person-heart-without-shapes.png'
import { anim, prog } from '../../../motion'
import { Shape } from '../../../primitives'
import { Fit, Line } from '../../../v2/kit'
import { useCopy, useGoTo, useLang } from '../../i18n'

// Formen wie in der Original-Illustration (640 × 540), beim Scrollen fliegen sie auseinander
const SHAPES = [
  {
    kind: 'dot',
    color: '#A7DDF7',
    size: 26,
    x: 385,
    y: 327,
    dx: -60,
    dy: -260,
    r: 0
  },
  {
    kind: 'sq',
    color: 'green',
    size: 34,
    x: 457,
    y: 322,
    dx: 140,
    dy: -300,
    r: 45
  },
  {
    kind: 'dot',
    color: 'yellow',
    size: 46,
    x: 510,
    y: 378,
    dx: 300,
    dy: -120,
    r: 0
  },
  {
    kind: 'tri',
    color: 'pink',
    size: 62,
    x: 444,
    y: 418,
    dx: 120,
    dy: 160,
    r: -30
  },
  {
    kind: 'ring',
    color: 'yellow',
    size: 70,
    x: 556,
    y: 465,
    dx: 360,
    dy: 140,
    r: 15
  },
  {
    kind: 'bar',
    color: '#DDF1FB',
    size: 80,
    x: 598,
    y: 372,
    dx: 420,
    dy: -40,
    r: -24
  },
  {
    kind: 'tri',
    color: 'blue',
    size: 30,
    x: 520,
    y: 300,
    dx: 260,
    dy: -360,
    r: 20
  },
  {
    kind: 'sq',
    color: 'pink',
    size: 22,
    x: 600,
    y: 430,
    dx: 480,
    dy: 60,
    r: 12
  }
]

const NARROW = { maxW: 0.78, maxH: 0.32 }

function Deco({ kind, color, size }) {
  if (kind === 'ring')
    return <span className='ch-ring' style={{ width: size, height: size }} />
  if (kind === 'bar')
    return (
      <span className='ch-bar' style={{ width: size, background: color }} />
    )
  return <Shape kind={kind} color={color} size={size} />
}

Deco.propTypes = {
  kind: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  size: PropTypes.number.isRequired
}

// Text links, Illustration rechts. Beim Scrollen fliegen die Formen aus der Hand in den Raum
export default function CandHero() {
  const c = useCopy('hero')
  const ui = useCopy('ui')
  const goTo = useGoTo()
  const lang = useLang()
  const demoHref = `${window.location.pathname}?v=demo${lang === 'en' ? '&lang=en' : ''}`

  return (
    <div className='cin-full ch-hero'>
      <div
        className='ch-hero__text'
        style={anim({ at: [-1, 0], out: [0.56, 0.7], oy: -80 })}
      >
        <div className='cin-eyebrow'>{c.eyebrow}</div>
        <h1 className='ch-hero__title'>
          {c.lines.map((line, i) => (
            <span
              key={line}
              className='ch-hero__line'
              style={
                i === 2
                  ? {
                      ...prog([0.04, 0.18]),
                      color:
                        'color-mix(in srgb, var(--blue) calc(var(--p) * 100%), var(--ink))'
                    }
                  : undefined
              }
            >
              {line}
            </span>
          ))}
        </h1>
        <p className='ch-hero__copy'>{c.text}</p>
        <div className='ch-hero__actions'>
          <button
            type='button'
            className='ch-btn'
            onClick={() => goTo('ablauf')}
          >
            {c.ctaFlow}
          </button>
          <button
            type='button'
            className='ch-btn ch-btn--ghost'
            onClick={() => goTo('faq')}
          >
            {c.ctaFaq}
          </button>
        </div>
        <a className='ch-company' href={demoHref}>
          <span className='ch-company__dot' />
          {c.company} <b>{c.companyLink}</b>
          <ArrowRightIcon width={14} height={14} />
        </a>
      </div>

      <Fit
        w={640}
        h={540}
        maxW={0.46}
        maxH={0.7}
        narrow={NARROW}
        className='ch-hero__art'
      >
        <div
          className='ch-hero__person'
          style={{
            ...prog([0.2, 0.7]),
            transform:
              'translateY(calc(var(--p) * 60px * var(--motion))) scale(calc(1 - 0.08 * var(--p)))',
            opacity: 'calc(1 - 0.9 * var(--p))'
          }}
        >
          <img src={person} alt='' width={399} height={536} />
        </div>
        {SHAPES.map((s, i) => (
          <div key={i} className='ch-shape' style={{ left: s.x, top: s.y }}>
            <div
              style={{
                ...prog([0.08 + (i % 4) * 0.03, 0.62 + (i % 3) * 0.04]),
                transform:
                  `translate(calc(var(--p) * ${s.dx}px * var(--motion)), calc(var(--p) * ${s.dy}px * var(--motion))) ` +
                  `rotate(calc(${s.r}deg + var(--p) * ${s.r * 4 + 90}deg * var(--motion))) scale(calc(1 + var(--p) * 0.6))`,
                opacity: i > 5 ? 'clamp(0, var(--p) * 4, 1)' : undefined
              }}
            >
              <Deco kind={s.kind} color={s.color} size={s.size} />
            </div>
          </div>
        ))}
      </Fit>

      <div
        className='cin-cue ch-cue'
        style={anim({ at: [-1, 0], out: [0.02, 0.08], oy: 20 })}
      >
        <ArrowDownIcon width={18} height={18} />
        {ui.cue}
      </div>

      <Line at={[0.6, 0.72]} className='ch-hero__after cin-display'>
        <span>{c.lines[2]}</span>
      </Line>
    </div>
  )
}
